import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Send,
  Paperclip,
  X,
  Eye,
  Shield
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const SupportTickets = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({
    open: 0,
    resolved: 0,
    avgResponseTime: '24-48 hrs'
  });
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [formData, setFormData] = useState({
    subject: '',
    category: 'remedy_assistance',
    message: '',
    attachments: []
  });
  const [uploading, setUploading] = useState(false);
  const [userReply, setUserReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replyAttachFile, setReplyAttachFile] = useState(null);
  const replyFileInputRef = React.useRef(null);

  const openTicketAttachment = async (ticketId, fileId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/support/ticket/${ticketId}/attachment/${fileId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fileId;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert('Could not download file: ' + e.message);
    }
  };

  const [inlineImages, setInlineImages] = useState({});
  const loadInlineImage = async (ticketId, fileId) => {
    const key = `${ticketId}-${fileId}`;
    if (inlineImages[key]) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/support/ticket/${ticketId}/attachment/${fileId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onloadend = () => setInlineImages(prev => ({ ...prev, [key]: reader.result }));
      reader.readAsDataURL(blob);
    } catch (e) { /* ignore */ }
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const categories = [
    { value: 'remedy_assistance', label: 'Remedy Assistance' },
    { value: 'template_mail', label: 'Template or Mail Issue' },
    { value: 'technical_bug', label: 'Technical Bug' },
    { value: 'subscription_billing', label: 'Subscription/Billing' },
    { value: 'feedback', label: 'Feedback/Suggestions' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/support/tickets`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data);
        
        // Calculate stats
        const openTickets = data.filter(t => t.status === 'open' || t.status === 'admin_replied').length;
        const resolvedTickets = data.filter(t => t.status === 'resolved').length;
        
        setStats({
          open: openTickets,
          resolved: resolvedTickets,
          avgResponseTime: '24-48 hrs'
        });
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast({
        title: "Error",
        description: "Failed to load support tickets",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file size (10MB max per file)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 10MB limit`,
          variant: "destructive"
        });
        return false;
      }
      return true;
    });

    // Limit to 5 files
    const currentFiles = formData.attachments.length;
    const remainingSlots = 5 - currentFiles;
    
    if (validFiles.length > remainingSlots) {
      toast({
        title: "Too many files",
        description: `Maximum 5 files allowed. You can add ${remainingSlots} more.`,
        variant: "destructive"
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles.slice(0, remainingSlots)]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.message) {
      toast({
        title: "Missing fields",
        description: "Please fill in subject and message",
        variant: "destructive"
      });
      return;
    }

    try {
      setUploading(true);

      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('subject', formData.subject);
      submitData.append('category', formData.category);
      submitData.append('message', formData.message);
      submitData.append('userEmail', user.email);
      submitData.append('userName', user.full_name);
      
      // Add files
      formData.attachments.forEach((file, index) => {
        submitData.append('attachments', file);
      });

      const response = await fetch(`${API_BASE}/api/support/ticket`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: submitData
      });

      if (response.ok) {
        toast({
          title: "Ticket created!",
          description: "Your support ticket has been submitted successfully."
        });
        
        // Reset form
        setFormData({
          subject: '',
          category: 'remedy_assistance',
          message: '',
          attachments: []
        });
        setShowCreateForm(false);
        fetchTickets();
      } else {
        throw new Error('Failed to create ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast({
        title: "Error",
        description: "Failed to create support ticket",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { color: 'bg-yellow-500', text: '🟡 Open', variant: 'default' },
      admin_replied: { color: 'bg-red-500', text: '🔴 Admin Replied', variant: 'destructive' },
      resolved: { color: 'bg-green-500', text: '🟢 Resolved', variant: 'success' },
      awaiting_admin: { color: 'bg-blue-500', text: '🟡 Awaiting Admin', variant: 'default' }
    };

    const config = statusConfig[status] || statusConfig.open;
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Support Tickets</h1>
          <p className="text-gray-600 mt-1">Get help from our support team</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={fetchTickets}
            variant="outline"
            disabled={loading}
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
            ) : (
              <Clock className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
          <Button 
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            New Ticket
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Open Tickets</CardTitle>
            <MessageSquare className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.open}</div>
            <p className="text-xs text-gray-500 mt-1">Active</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Resolved Tickets</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
            <p className="text-xs text-gray-500 mt-1">Closed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Avg. Response Time</CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.avgResponseTime}</div>
            <p className="text-xs text-gray-500 mt-1">Typical</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Status</CardTitle>
            <Shield className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold text-purple-600">Encrypted</div>
            <p className="text-xs text-gray-500 mt-1">& Private</p>
          </CardContent>
        </Card>
      </div>

      {/* Create Ticket Form */}
      {showCreateForm && (
        <Card className="border-2 border-blue-500">
          <CardHeader>
            <CardTitle>Start a New Support Ticket</CardTitle>
            <CardDescription>We'll get back to you within 24-48 hours</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  placeholder="Short title of your issue"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  placeholder="Describe your issue in detail..."
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Attach Files (Optional)</Label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      multiple
                      accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.gif"
                      onChange={handleFileUpload}
                      disabled={formData.attachments.length >= 5}
                      className="hidden"
                      id="file-upload"
                    />
                    <label 
                      htmlFor="file-upload"
                      className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      Choose Files
                    </label>
                    <span className="text-sm text-gray-500">
                      Max 5 files, 10MB each
                    </span>
                  </div>

                  {formData.attachments.length > 0 && (
                    <div className="space-y-2">
                      {formData.attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-700">{file.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  disabled={uploading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Ticket
                    </>
                  )}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Tickets List */}
      <Card>
        <CardHeader>
          <CardTitle>My Tickets</CardTitle>
          <CardDescription>View and manage your support requests</CardDescription>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets yet</h3>
              <p className="text-gray-600 mb-4">Create your first support ticket to get help</p>
              <Button onClick={() => setShowCreateForm(true)}>
                Create Ticket
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tickets.map((ticket) => (
                    <tr key={ticket.ticket_id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm font-medium text-blue-600">
                        #{ticket.ticket_id.slice(0, 8)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {ticket.subject}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {getStatusBadge(ticket.status)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {formatDate(ticket.updated_at)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {formatDate(ticket.created_at)}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedTicket(ticket)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Modal — Conversation Style */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-3xl w-full max-h-[85vh] flex flex-col">
            <CardHeader className="border-b flex-shrink-0">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-xl">Ticket #{selectedTicket.ticket_id.slice(0, 8)}</CardTitle>
                  <CardDescription className="mt-1">{selectedTicket.subject}</CardDescription>
                  <div className="mt-2 flex items-center gap-2">
                    {getStatusBadge(selectedTicket.status)}
                    <span className="text-xs text-gray-500">
                      Created: {formatDate(selectedTicket.created_at)}
                    </span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedTicket(null); setUserReply(''); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="user-ticket-conversation">
              {/* Original message */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-900">{selectedTicket.user_name} (You)</span>
                  <span className="text-xs text-gray-500">{formatDate(selectedTicket.created_at)}</span>
                </div>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedTicket.message}</p>
              </div>

              {/* Conversation thread */}
              {selectedTicket.responses?.map((resp, idx) => (
                <div
                  key={idx}
                  className={`rounded-lg p-4 ${resp.is_user ? 'bg-gray-50' : 'bg-blue-50 border-l-4 border-blue-500 ml-4'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-semibold ${resp.is_user ? 'text-gray-900' : 'text-blue-900'}`}>
                      {resp.is_user ? `${resp.admin_name} (You)` : `${resp.admin_name || 'Admin'}`}
                    </span>
                    <span className="text-xs text-gray-500">
                      {resp.created_at ? formatDate(resp.created_at) : ''}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{resp.response}</p>
                  {/* Render attachments in responses */}
                  {resp.attachments?.map((att, aidx) => {
                    const isImage = att.content_type?.startsWith('image/');
                    const imgKey = `${selectedTicket.ticket_id}-${att.file_id}`;
                    if (isImage && !inlineImages[imgKey]) loadInlineImage(selectedTicket.ticket_id, att.file_id);
                    return (
                      <div key={aidx} className="mt-2 border rounded p-2 bg-white">
                        {isImage && inlineImages[imgKey] ? (
                          <div>
                            <img src={inlineImages[imgKey]} alt={att.filename} className="max-h-48 rounded" />
                            <button onClick={() => openTicketAttachment(selectedTicket.ticket_id, att.file_id)} className="text-xs text-blue-600 hover:underline mt-1">
                              Download {att.filename}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => openTicketAttachment(selectedTicket.ticket_id, att.file_id)}
                            className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                          >
                            <Paperclip className="w-4 h-4" />
                            {att.filename}
                            <span className="text-xs text-gray-400">({(att.size / 1024).toFixed(1)} KB)</span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}

              {(!selectedTicket.responses || selectedTicket.responses.length === 0) && (
                <div className="text-center py-6">
                  <AlertCircle className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">No responses yet</p>
                  <p className="text-xs text-gray-400 mt-1">We'll notify you via email when an admin replies</p>
                </div>
              )}

              {/* Attachments */}
              {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Attachments</p>
                  <div className="space-y-2">
                    {selectedTicket.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                        <Paperclip className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">{attachment.filename}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>

            {/* Reply input — always visible while ticket is not closed/resolved */}
            {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
              <div className="border-t p-4 flex-shrink-0 space-y-2">
                {/* Attached file preview */}
                {replyAttachFile && (
                  <div className="flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-sm">
                    <Paperclip className="w-3 h-3 text-blue-600" />
                    <span className="text-blue-800 truncate flex-1">{replyAttachFile.name}</span>
                    <button onClick={() => setReplyAttachFile(null)} className="text-red-500 hover:text-red-700 text-xs font-bold">X</button>
                  </div>
                )}
                <input
                  type="file"
                  ref={replyFileInputRef}
                  className="hidden"
                  accept=".pdf,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 10 * 1024 * 1024) { alert('File too large. Max 10MB.'); return; }
                      setReplyAttachFile(file);
                    }
                    e.target.value = '';
                  }}
                />
                <div className="flex gap-2">
                  <textarea
                    value={userReply}
                    onChange={(e) => setUserReply(e.target.value)}
                    placeholder="Type your reply..."
                    rows={2}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-black resize-none"
                    data-testid="user-ticket-reply-input"
                  />
                  <div className="flex flex-col gap-1 self-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => replyFileInputRef.current?.click()}
                      title="Attach file"
                      data-testid="user-ticket-attach-btn"
                    >
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Button
                      className="bg-blue-600 hover:bg-blue-700"
                      size="sm"
                      disabled={(!userReply.trim() && !replyAttachFile) || sendingReply}
                      onClick={async () => {
                        if (!userReply.trim() && !replyAttachFile) return;
                        setSendingReply(true);
                        try {
                          const token = localStorage.getItem('token');

                          if (replyAttachFile) {
                            // Send with file
                            const formData = new FormData();
                            formData.append('file', replyAttachFile);
                            formData.append('message', userReply.trim());
                            const res = await fetch(`${API_BASE}/api/support/ticket/${selectedTicket.ticket_id}/reply-with-attachment`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` },
                              body: formData
                            });
                            if (!res.ok) {
                              const err = await res.json().catch(() => ({}));
                              alert(err.detail || 'Failed to send reply');
                              setSendingReply(false);
                              return;
                            }
                          } else {
                            // Text only
                            const res = await fetch(`${API_BASE}/api/support/ticket/${selectedTicket.ticket_id}/reply`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ message: userReply.trim() })
                            });
                            if (!res.ok) {
                              const err = await res.json().catch(() => ({}));
                              alert(err.detail || 'Failed to send reply');
                              setSendingReply(false);
                              return;
                            }
                          }

                          setUserReply('');
                          setReplyAttachFile(null);
                          await fetchTickets();
                          const freshRes = await fetch(`${API_BASE}/api/support/ticket/${selectedTicket.ticket_id}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (freshRes.ok) {
                            const fresh = await freshRes.json();
                            setSelectedTicket(fresh);
                          }
                        } catch (e) {
                          alert('Error sending reply');
                        } finally {
                          setSendingReply(false);
                        }
                      }}
                      data-testid="user-ticket-send-reply-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
      </div>
    </div>
  );
};

export default SupportTickets;
